import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  createInstantConsultationSection,
  deleteInstantConsultationSection,
  fetchInstantConsultationSections,
  updateInstantConsultationSection,
  type InstantConsultationSectionDto,
} from '../../../services/MasterAdmin/instantConsultationSectionService';

const getApiErrorMessage = (e: unknown, fallback: string): string => {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message ===
      'string'
  ) {
    return String((e as { response?: { data?: { message?: string } } }).response?.data?.message);
  }
  return fallback;
};

interface SectionForm {
  title: string;
  description: string;
  sortOrder: string;
  isActive: boolean;
}

const emptySectionForm = (): SectionForm => ({
  title: '',
  description: '',
  sortOrder: '0',
  isActive: true,
});

interface Props {
  onSectionsChange?: () => void;
  /** Hide title block when the parent page already shows a page heading. */
  hideHeader?: boolean;
}

const InstantConsultationSectionsPanel: React.FC<Props> = ({
  onSectionsChange,
  hideHeader = false,
}) => {
  const [sections, setSections] = useState<InstantConsultationSectionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<InstantConsultationSectionDto | null>(null);
  const [form, setForm] = useState<SectionForm>(emptySectionForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InstantConsultationSectionDto | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchInstantConsultationSections();
      setSections(res.data || []);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Failed to load sections.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptySectionForm());
    setDrawerOpen(true);
  };

  const openEdit = (row: InstantConsultationSectionDto) => {
    setEditing(row);
    setForm({
      title: row.title,
      description: row.description || '',
      sortOrder: String(row.sortOrder),
      isActive: row.isActive,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    const title = form.title.trim();
    if (!title) {
      setError('Section title is required.');
      return;
    }
    const duplicate = sections.find(
      (section) =>
        section.id !== editing?.id &&
        section.title.trim().toLowerCase() === title.toLowerCase()
    );
    if (duplicate) {
      setError(`A section titled "${title}" already exists.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        title,
        description: form.description.trim() || undefined,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
        isActive: form.isActive,
      };
      if (editing) {
        await updateInstantConsultationSection(editing.id, body);
      } else {
        await createInstantConsultationSection(body);
      }
      setDrawerOpen(false);
      await load();
      onSectionsChange?.();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setError(null);
    try {
      await deleteInstantConsultationSection(deleteTarget.id);
      setDeleteTarget(null);
      await load();
      onSectionsChange?.();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Delete failed.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {hideHeader ? (
          <div />
        ) : (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Instant consultation sections
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create sections first (e.g. Primary Care), then assign specialities to each section.
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Add section
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : sections.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            No sections yet. Add one to group specialities on the instant consultation screen.
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Title</th>
                <th className="px-4 py-3 text-left font-semibold">Order</th>
                <th className="px-4 py-3 text-left font-semibold">Active</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sections.map((s) => (
                <tr key={s.id}>
                  <td className="px-4 py-3 font-medium">{s.title}</td>
                  <td className="px-4 py-3">{s.sortOrder}</td>
                  <td className="px-4 py-3">{s.isActive ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      className="mr-1 rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => openEdit(s)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      onClick={() => setDeleteTarget(s)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b px-5 py-4 dark:border-gray-700">
              <h3 className="font-bold">{editing ? 'Edit section' : 'Add section'}</h3>
              <button type="button" onClick={() => setDrawerOpen(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              <label className="block text-sm">
                <span className="font-medium">Title *</span>
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Primary Care"
                  className="mt-1 w-full rounded-lg border px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Description</span>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="mt-1 w-full rounded-lg border px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium">Sort order</span>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  className="mt-1 w-full rounded-lg border px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4 dark:border-gray-700">
              <button
                type="button"
                className="rounded-lg border px-4 py-2 text-sm"
                onClick={() => setDrawerOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </aside>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete section?"
        message={
          deleteTarget
            ? `Delete section "${deleteTarget.title}"? Specialities in this section will become unsectioned.`
            : ''
        }
        tone="danger"
        confirmLabel="Delete"
        busy={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteTarget(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
};

export default InstantConsultationSectionsPanel;
